import { Button } from '@mui/material';
import { toastError, toastSuccess } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { find, isEmpty } from 'lodash';
import React, { useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import AddFallRiskFormFields from '../../../../../components/AddFallRiskDialog/AddFallRiskFormFields';
import formStyles from '../../../../../components/AddFallRiskDialog/index.module.scss';
import useMixpanel from '../../../../../hooks/useMixpanel';
import useClinicalInfoService from '../../../../../services/patientProfileService/clinicalInfo';
import useFallService from '../../../../../services/patientProfileService/clinicalInfo/fall';
import type { IFallOption } from '../../../../../services/patientProfileService/clinicalInfo/types';
import { replaceStringNull } from '../../../../../utils/noneOptionUtil';
import { eventModalContext } from '../../../Timeline';
import styles from './index.module.scss';

export default function AddFallForm() {
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const { data } = useClinicalInfoService(patientUuid);
  const { addFall, editFall } = useFallService(patientUuid);
  const { trackClick } = useMixpanel();
  const { eventUuid, setEventUuid } = useContext(eventModalContext);
  const newItem = eventUuid === 'none';
  const fallData = find(data?.falls, ['uuid', eventUuid]) ?? ({} as IFallOption);

  const methods = useForm({
    ...(!isEmpty(fallData) && {
      defaultValues: {
        ...fallData,
      },
    }),
  });
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;
  const onSubmit = async (formData: Partial<IFallOption>) => {
    const witnessed = replaceStringNull(formData?.witnessed as string) as boolean;
    try {
      if (newItem) {
        await addFall({
          ...formData,
          witnessed,
          patientUuid,
        });
        trackClick({
          action: 'add_event',
        });
      } else {
        await editFall({
          data: {
            ...formData,
            witnessed,
            patientUuid,
          },
          uuid: eventUuid as string,
        });
      }
      toastSuccess({ content: `Surgery ${newItem ? 'created' : 'edited'} successfully!` });
    } catch {
      toastError({ content: `Failed to ${newItem ? 'create' : 'edit'} surgery!` });
    } finally {
      setEventUuid();
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className={classNames(
          'flex flex-col px-4',
          formStyles.addFallform,
          formStyles.blackFields,
        )}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AddFallRiskFormFields />
        {!isEmpty(errors) ? (
          <div className={classNames('color-error', styles.error)}>
            Please enter required fields
          </div>
        ) : null}
        <div className={classNames('flex justify-center', styles.btnContainer)}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            classes={{ root: styles.addBtn }}
          >
            {isSubmitting ? 'Loading ...' : newItem ? 'Add' : 'Save'}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
